import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQueries } from '@tanstack/react-query';
import SheetModal from '@/components/General/SheetModal';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Chip from '@/components/General/Chip';
import Unicon from '@/components/General/Unicon';
import useLoader from '@/utils/hooks/useLoader';
import { exportHTMLToPDFWithLoader } from '@/utils/exportPDF';
import { DateTime } from 'luxon';
import { getUpcomingEventInstancesWithTasks } from '@/data/events';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { buildScheduleGridData } from '@/utils/buildScheduleGridData';

const MAX_EVENTS_PER_TABLE = 6;

function SchedulePDFExport({ isOpen, onClose, defaultEvent, organization }) {
    const loader = useLoader();

    const [selectedEvents, setSelectedEvents] = useState(() => defaultEvent ? [defaultEvent] : []);
    const [selectedTeams, setSelectedTeams] = useState(new Set());
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    // Reset selections when modal opens (dep on id string, not object ref)
    useEffect(() => {
        if (isOpen) {
            setSelectedEvents(defaultEvent ? [defaultEvent] : []);
            setSelectedTeams(new Set());
            setDateRange({ start: null, end: null });
        }
    }, [isOpen, defaultEvent?.id]);

    // Fetch instances for each selected event
    const instanceResults = useQueries({
        queries: selectedEvents.map(ev => ({
            queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(ev.id),
            queryFn: () => getUpcomingEventInstancesWithTasks(ev.id),
            enabled: !!ev?.id,
            staleTime: Infinity,
        })),
    });

    // Stable keys so memos don't recompute on every useQueries array ref change
    const instanceDataKey = instanceResults.map(r => r?.dataUpdatedAt || 0).join(',');
    const selectedEventsKey = selectedEvents.map(ev => ev?.id).join(',');

    // Build per-event data: { event, instances, gridData }
    const eventData = useMemo(() => {
        return selectedEvents.map((ev, idx) => {
            const instances = instanceResults[idx]?.data || [];
            const gridData = buildScheduleGridData(instances);
            return { event: ev, instances, gridData };
        });
    }, [selectedEventsKey, instanceDataKey]);

    // All unique teams across selected events
    const allTeams = useMemo(() => {
        const teams = new Set();
        eventData.forEach(({ gridData }) => {
            gridData.forEach(g => teams.add(g.teamName));
        });
        return Array.from(teams).sort();
    }, [eventData]);

    // Ref to latest allTeams so effects can depend on a stable string key
    const allTeamsRef = React.useRef(allTeams);
    allTeamsRef.current = allTeams;
    const allTeamsKey = allTeams.join(',');

    // Auto-select all teams on first population + prune invalid teams when list changes
    const didAutoSelectTeams = React.useRef(false);
    useEffect(() => {
        if (!isOpen) {
            didAutoSelectTeams.current = false;
            return;
        }
        const teams = allTeamsRef.current;
        if (teams.length > 0 && !didAutoSelectTeams.current) {
            didAutoSelectTeams.current = true;
            setSelectedTeams(new Set(teams));
        } else {
            const valid = new Set(teams);
            setSelectedTeams(prev => {
                const next = new Set();
                prev.forEach(t => { if (valid.has(t)) next.add(t); });
                return next;
            });
        }
    }, [isOpen, allTeamsKey]);

    const toggleTeam = useCallback((teamName) => {
        setSelectedTeams((prev) => {
            const next = new Set(prev);
            if (next.has(teamName)) next.delete(teamName);
            else next.add(teamName);
            return next;
        });
    }, []);

    const selectAllTeams = useCallback(() => setSelectedTeams(new Set(allTeams)), [allTeams]);
    const clearAllTeams = useCallback(() => setSelectedTeams(new Set()), []);

    const formatInstanceDate = (d) => {
        if (!d) return '';
        const dt = d instanceof Date ? DateTime.fromJSDate(d) : DateTime.fromISO(d);
        return dt.toFormat('MMM d');
    };

    const formatInstanceDay = (d) => {
        if (!d) return '';
        const dt = d instanceof Date ? DateTime.fromJSDate(d) : DateTime.fromISO(d);
        return dt.toFormat('ccc');
    };

    const formatInstanceTime = (d) => {
        if (!d) return '';
        const dt = d instanceof Date ? DateTime.fromJSDate(d) : DateTime.fromISO(d);
        return dt.toFormat('h:mm a');
    };

    const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    };

    // Filter instances by date range
    const isInstanceInRange = useCallback((inst) => {
        if (!dateRange.start && !dateRange.end) return true;
        const instDate = new Date(inst.startTime);
        if (dateRange.start) {
            const startBound = new Date(dateRange.start);
            startBound.setHours(0, 0, 0, 0);
            if (instDate < startBound) return false;
        }
        if (dateRange.end) {
            const endBound = new Date(dateRange.end);
            endBound.setHours(23, 59, 59, 999);
            if (instDate > endBound) return false;
        }
        return true;
    }, [dateRange]);

    // Build filtered per-event data for export
    const filteredEventData = useMemo(() => {
        return eventData.map(({ event, instances, gridData }) => {
            const filteredInstances = instances.filter(isInstanceInRange);
            const filteredGrid = gridData
                .filter(team => selectedTeams.has(team.teamName))
                .map(team => ({
                    ...team,
                    tasks: team.tasks.map(taskData => ({
                        ...taskData,
                        instances: new Map(
                            Array.from(taskData.instances.entries())
                                .filter(([instId]) => filteredInstances.some(fi => fi.id === instId))
                        ),
                    })).filter(t => t.instances.size > 0),
                }))
                .filter(team => team.tasks.length > 0);
            return { event, instances: filteredInstances, gridData: filteredGrid };
        }).filter(({ instances, gridData }) => instances.length > 0 && gridData.length > 0);
    }, [eventData, selectedTeams, isInstanceInRange]);

    const canExport = selectedEvents.length > 0 && selectedTeams.size > 0 && filteredEventData.length > 0;

    const generateHTML = () => {
        if (!filteredEventData.length) {
            return `<html><body><p style="text-align:center;padding:40px;font-family:sans-serif;">No data matches the selected filters.</p></body></html>`;
        }

        const logoHtml = organization?.logoPublicUrlLongDark
            ? `<img src="${organization.logoPublicUrlLongDark}" class="logo-img" alt="" />`
            : '';

        const titleText = filteredEventData.length === 1
            ? filteredEventData[0].event?.name || 'Volunteer Schedule'
            : 'Volunteer Schedule';

        const eventSections = filteredEventData.map(({ event, instances, gridData }, eventIdx) => {
            const eventName = event?.name || 'Event Schedule';
            const instanceChunks = chunkArray(instances, MAX_EVENTS_PER_TABLE);
            const dateRangeText = instances.length === 1
                ? formatInstanceDate(instances[0].startTime)
                : `${formatInstanceDate(instances[0].startTime)} – ${formatInstanceDate(instances[instances.length - 1].startTime)}`;

            const teamSections = gridData.map((team) => {
                const tablesHtml = instanceChunks.map((chunk, chunkIdx) => {
                    const chunkLabel = instanceChunks.length > 1
                        ? `<div class="chunk-label">Dates ${chunkIdx + 1} of ${instanceChunks.length}</div>`
                        : '';

                    const dateHeaders = chunk.map(inst => `
                        <th>
                            <div class="day">${formatInstanceDay(inst.startTime)}</div>
                            <div class="date">${formatInstanceDate(inst.startTime)}</div>
                            <div class="time">${formatInstanceTime(inst.startTime)}</div>
                        </th>
                    `).join('');

                    const rows = team.tasks.map(taskData => {
                        const cells = chunk.map(inst => {
                            const cellData = taskData.instances.get(inst.id);
                            const assignees = cellData?.assignees || [];
                            if (assignees.length > 0) {
                                const names = assignees.map(a =>
                                    `<div class="assignee-name">${(a.Person?.firstName || '').trim()} ${(a.Person?.lastName || '').trim()}</div>`
                                ).join('');
                                return `<td>${names}</td>`;
                            }
                            if (cellData) {
                                return `<td class="unassigned">—</td>`;
                            }
                            return `<td class="notask"></td>`;
                        }).join('');

                        return `
                            <tr>
                                <td class="task-name">${taskData.task.title}</td>
                                ${cells}
                            </tr>
                        `;
                    }).join('');

                    return `
                        <div class="table-block">
                            ${chunkLabel}
                            <div class="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th class="task-col">Task</th>
                                            ${dateHeaders}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${rows}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    `;
                }).join('');

                return `
                    <div class="team-section">
                        <div class="team-header">
                            <span class="team-name">${team.teamName}</span>
                            <span class="team-meta">${team.tasks.length} task${team.tasks.length !== 1 ? 's' : ''}</span>
                        </div>
                        ${tablesHtml}
                    </div>
                `;
            }).join('');

            const eventBreak = eventIdx === 0 ? '' : ' page-break-before';

            return `
                <div class="event-section${eventBreak}">
                    <div class="event-header">
                        <h2>${eventName}</h2>
                        <div class="event-date-range">${dateRangeText}</div>
                    </div>
                    ${teamSections}
                </div>
            `;
        }).join('');

        return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8">
    <meta name="color-scheme" content="light">
    <title>${titleText} – Volunteer Schedule</title>
    <style>
        @page { size: letter landscape; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { color-scheme: light; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #1f2937 !important;
            background: #ffffff !important;
            padding: 0;
        }
        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-bottom: 22px;
            padding-bottom: 12px;
            border-bottom: 2px solid #4f46e5;
            background: #ffffff !important;
        }
        .page-header .left { flex: 1; }
        .page-header h1 {
            font-size: 20pt;
            font-weight: 700;
            color: #111827 !important;
            margin: 0;
        }
        .page-header .subtitle {
            font-size: 12pt;
            color: #6b7280 !important;
            margin-top: 4px;
        }
        .page-header .right {
            text-align: right;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 6px;
        }
        .page-header .logo-img {
            max-height: 36px;
            max-width: 180px;
            object-fit: contain;
        }
        .page-header .meta {
            font-size: 10pt;
            color: #6b7280 !important;
        }
        .event-section { margin-bottom: 28px; }
        .page-break-before { page-break-before: always; }
        .event-header {
            margin-bottom: 18px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e5e7eb;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: avoid;
        }
        .event-header h2 {
            font-size: 16pt;
            font-weight: 700;
            color: #374151 !important;
            margin: 0;
        }
        .event-header .event-date-range {
            font-size: 11pt;
            color: #6b7280 !important;
            margin-top: 4px;
        }
        .team-section { margin-bottom: 28px; page-break-inside: avoid; break-inside: avoid; }
        .team-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: #f3f4f6 !important;
            padding: 10px 14px;
            border-radius: 6px 6px 0 0;
            border: 1px solid #e5e7eb;
            border-bottom: 0;
        }
        .team-header .team-name {
            font-size: 13pt;
            font-weight: 700;
            color: #374151 !important;
            text-transform: uppercase;
            letter-spacing: 0.3px;
        }
        .team-header .team-meta {
            font-size: 10pt;
            color: #9ca3af !important;
        }
        .chunk-label {
            font-size: 10pt;
            color: #6b7280 !important;
            padding: 8px 14px;
            background: #f9fafb !important;
            border-left: 1px solid #e5e7eb;
            border-right: 1px solid #e5e7eb;
        }
        .table-block {
            margin-bottom: 16px;
            page-break-inside: avoid;
            break-inside: avoid;
        }
        .table-wrap { overflow-x: auto; }
        table {
            table-layout: fixed;
            width: 100%;
            border-collapse: collapse;
            page-break-inside: avoid;
            break-inside: avoid;
        }
        th {
            background: #e0e7ff !important;
            color: #4338ca !important;
            font-weight: 600;
            padding: 8px 12px;
            text-align: center;
            border: 1px solid #d1d5db;
            font-size: 10pt;
            vertical-align: middle;
        }
        th .day {
            font-size: 9pt;
            text-transform: uppercase;
            color: #6b7280 !important;
            margin-bottom: 2px;
        }
        th .date {
            font-size: 11pt;
            font-weight: 700;
        }
        th .time {
            font-size: 8pt;
            color: #6b7280 !important;
            margin-top: 2px;
        }
        th.task-col {
            width: 160px;
            text-align: left;
            background: #e0e7ff !important;
            color: #4338ca !important;
        }
        th:not(.task-col) {
            overflow-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
        }
        td {
            padding: 8px;
            border: 1px solid #e5e7eb;
            font-size: 10pt;
            vertical-align: middle;
            overflow-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
        }
        td.task-name {
            font-weight: 600;
            color: #1f2937 !important;
            background: #fafafa !important;
            width: 160px;
        }
        td .assignee-name {
            margin-bottom: 2px;
            overflow-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
        }
        td .assignee-name:last-child { margin-bottom: 0; }
        td.unassigned {
            color: #9ca3af !important;
            text-align: center;
            font-style: italic;
        }
        td.notask { background: #f9fafb !important; }
        tr:nth-child(even) td { background: #fcfcfd !important; }
        tr:nth-child(even) td.task-name { background: #f5f7fa !important; }
        .footer {
            margin-top: 30px;
            padding-top: 10px;
            border-top: 1px solid #e5e7eb;
            font-size: 10pt;
            color: #9ca3af !important;
            text-align: center;
            background: #ffffff !important;
        }
    </style>
</head>
<body>
    <div class="page-header">
        <div class="left">
            <h1>${titleText}</h1>
            <div class="subtitle">Volunteer Schedule${filteredEventData.length > 1 ? ` · ${filteredEventData.length} Events` : ''}</div>
        </div>
        <div class="right">
            ${logoHtml}
            <div class="meta">Generated ${DateTime.now().toLocaleString(DateTime.DATETIME_MED)}</div>
        </div>
    </div>

    ${eventSections}

    <div class="footer">
        Generated by Reach CHMS
    </div>
</body>
</html>
        `;
    };

    const handleExport = async () => {
        if (!canExport) return;
        const html = generateHTML();
        const filename = filteredEventData.length === 1
            ? `${filteredEventData[0].event?.name || 'Schedule'}_VolunteerSchedule`
            : 'MultiEvent_VolunteerSchedule';
        await exportHTMLToPDFWithLoader({
            html,
            filename,
            title: 'Export Volunteer Schedule',
            orientation: 'landscape',
        }, loader);
        onClose();
    };

    const isLoadingInstances = instanceResults.some(r => r?.isLoading);

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Export Schedule"
            cancelText="Cancel"
            confirmText="Download"
            onConfirm={handleExport}
        >
            <View style={styles.modalContent}>
                <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
                    {/* Events */}
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Events</Text>
                        <SheetSelector
                            type="events"
                            title="Select Events"
                            multiple
                            value={selectedEvents}
                            onChange={setSelectedEvents}
                            noDefaultTruncated
                        >
                            <View style={styles.eventPickerTrigger}>
                                <Unicon name="calendar" size={16} colorVariant="primary" />
                                <Text style={styles.eventPickerText}>
                                    {selectedEvents.length === 0
                                        ? 'Select Events'
                                        : selectedEvents.length === 1
                                            ? selectedEvents[0]?.name
                                            : `${selectedEvents.length} Events Selected`}
                                </Text>
                                <Unicon name="chevron-down" size={14} colorVariant="textSecondary" />
                            </View>
                        </SheetSelector>
                    </View>

                    {/* Teams */}
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionTitle}>Teams</Text>
                            <View style={styles.sectionActions}>
                                <Pressable onPress={selectAllTeams}>
                                    <Text style={styles.actionText}>All</Text>
                                </Pressable>
                                <Pressable onPress={clearAllTeams}>
                                    <Text style={styles.actionText}>None</Text>
                                </Pressable>
                            </View>
                        </View>
                        <View style={styles.chipRow}>
                            {allTeams.map((team) => {
                                const isSelected = selectedTeams.has(team);
                                return (
                                    <Pressable
                                        key={team}
                                        onPress={() => toggleTeam(team)}
                                        style={styles.teamChipWrapper}
                                    >
                                        <Chip
                                            colorVariant={isSelected ? 'primary' : 'textSecondary'}
                                            icon={isSelected ? 'checkmark' : null}
                                            style={styles.teamChip}
                                            textStyle={styles.teamChipText}
                                        >
                                            {team}
                                        </Chip>
                                    </Pressable>
                                );
                            })}
                            {allTeams.length === 0 && !isLoadingInstances && (
                                <Text style={styles.emptyText}>No teams available. Select events first.</Text>
                            )}
                            {allTeams.length === 0 && isLoadingInstances && (
                                <Text style={styles.emptyText}>Loading teams...</Text>
                            )}
                        </View>
                    </View>

                    {/* Date Range */}
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Date Range</Text>
                        <Text style={styles.sectionHint}>Leave blank to include all upcoming dates for the selected events.</Text>
                        <View style={styles.dateRangeRow}>
                            <View style={styles.dateRangeInput}>
                                <DatePickerInput
                                    title="Start Date"
                                    mode="date"
                                    value={dateRange.start}
                                    maximumDate={dateRange.end || null}
                                    onUpdate={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
                                />
                            </View>
                            <View style={styles.dateRangeInput}>
                                <DatePickerInput
                                    title="End Date"
                                    mode="date"
                                    value={dateRange.end}
                                    minimumDate={dateRange.start || null}
                                    onUpdate={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
                                />
                            </View>
                        </View>
                    </View>
                </ScrollView>
            </View>
        </SheetModal>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    modalContent: {
        flex: 1,
        padding: 16,
    },
    scrollContent: {
        gap: 20,
        paddingBottom: 20,
    },
    section: {
        gap: 10,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    sectionTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    sectionHint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    sectionActions: {
        flexDirection: 'row',
        gap: 16,
    },
    actionText: {
        fontSize: 13,
        color: colors.primary,
        fontFamily: fonts.heading,
    },
    eventPickerTrigger: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
    },
    eventPickerText: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.heading,
        flex: 1,
    },
    chipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    teamChipWrapper: {
        // Wrapper for tappable Chip
    },
    teamChip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
    },
    teamChipText: {
        fontSize: 13,
    },
    emptyText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    dateRangeRow: {
        flexDirection: 'row',
        gap: 12,
    },
    dateRangeInput: {
        flex: 1,
    },
}));

export default SchedulePDFExport;
